/**
 * Full Page Scroll Hook using GSAP Observer
 *
 * This hook provides better cross-device compatibility for full-page vertical scrolling
 * by leveraging GSAP's Observer plugin which handles:
 * - Wheel events with proper momentum/inertia detection for trackpads
 * - Touch events with unified handling across iOS/Android
 * - Pointer events for stylus and hybrid devices
 * - Keyboard navigation
 *
 * This replaces the custom event handlers in useFullPageScroll.ts with
 * GSAP's battle-tested implementation.
 */

import { RefObject, useCallback, useEffect, useRef, useState } from "react"
import { Observer } from "../lib/gsap/config"

export interface SectionChangeInfo {
  current: number
  prev: number | null
  next: number | null
}

interface UseFullPageScrollGsapOptions {
  totalSections: number
  initialSection?: number
  containerRef?: RefObject<HTMLElement | null>
  /** Optional array of hash strings (without #) for each section, e.g., ['home', 'services', 'clients'] */
  sectionHashes?: string[]
  /** Callback fired before section transition starts, receives { current, prev, next } indices */
  onSectionChange?: (info: SectionChangeInfo) => void
  /** Wheel sensitivity threshold (higher = less sensitive, default: 100) */
  wheelThreshold?: number
  /** Touch drag threshold in pixels (default: 50) */
  touchThreshold?: number
}

interface UseFullPageScrollGsapReturn {
  currentSection: number
  isAnimating: boolean
  prefersReducedMotion: boolean
  goToSection: (index: number) => void
  nextSection: () => void
  prevSection: () => void
}

const COOLDOWN_MS = 800

/**
 * Hook for managing full-page section navigation using GSAP Observer.
 * Provides better trackpad momentum handling and unified touch/pointer support.
 */
export function useFullPageScrollGsap({
  totalSections,
  initialSection = 0,
  containerRef,
  sectionHashes,
  onSectionChange,
  wheelThreshold = 100,
  touchThreshold = 50,
}: UseFullPageScrollGsapOptions): UseFullPageScrollGsapReturn {
  // Helper to get initial section from hash (if valid)
  const getInitialSectionFromHash = (): number => {
    if (typeof window === "undefined" || !sectionHashes) {
      return Math.max(0, Math.min(initialSection, totalSections - 1))
    }
    const hash = window.location.hash.slice(1) // Remove #
    if (hash) {
      const index = sectionHashes.indexOf(hash)
      if (index !== -1) {
        return index
      }
    }
    return Math.max(0, Math.min(initialSection, totalSections - 1))
  }

  const [currentSection, setCurrentSection] = useState<number>(
    getInitialSectionFromHash
  )
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(
    () => {
      if (typeof window === "undefined") return false
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    }
  )

  const cooldownRef = useRef<number | null>(null)
  const isUpdatingFromHashRef = useRef<boolean>(false)
  const hasCalledInitialCallbackRef = useRef<boolean>(false)

  // Use refs for values that GSAP Observer callbacks need to access
  // This prevents stale closures in the Observer callbacks
  const currentSectionRef = useRef(currentSection)
  const isAnimatingRef = useRef(isAnimating)
  const prefersReducedMotionRef = useRef(prefersReducedMotion)

  // Keep refs in sync with state
  useEffect(() => {
    currentSectionRef.current = currentSection
  }, [currentSection])

  useEffect(() => {
    isAnimatingRef.current = isAnimating
  }, [isAnimating])

  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion
  }, [prefersReducedMotion])

  // Call onSectionChange on initial mount with current section
  useEffect(() => {
    if (onSectionChange && !hasCalledInitialCallbackRef.current) {
      hasCalledInitialCallbackRef.current = true
      const initial = getInitialSectionFromHash()
      onSectionChange({
        current: initial,
        prev: null,
        next: null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for prefers-reduced-motion changes
  useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener("change", handleChange)

    return () => {
      mediaQuery.removeEventListener("change", handleChange)
    }
  }, [])

  // Core navigation function
  const goToSectionInternal = useCallback(
    (index: number) => {
      // Validate index bounds
      if (index < 0 || index >= totalSections) {
        return
      }

      // Check cooldown
      if (isAnimatingRef.current) {
        return
      }

      // Same section, no-op
      if (index === currentSectionRef.current) {
        return
      }

      const prevSection = currentSectionRef.current

      // Call onSectionChange callback before starting transition
      if (onSectionChange) {
        onSectionChange({
          current: index,
          prev: prevSection,
          next: null,
        })
      }

      setIsAnimating(true)
      setCurrentSection(index)

      // Clear any existing cooldown
      if (cooldownRef.current !== null) {
        window.clearTimeout(cooldownRef.current)
      }

      // Set cooldown timer (instant when reduced motion is enabled)
      const cooldownDuration = prefersReducedMotionRef.current ? 0 : COOLDOWN_MS
      cooldownRef.current = window.setTimeout(() => {
        setIsAnimating(false)
        cooldownRef.current = null
      }, cooldownDuration)
    },
    [totalSections, onSectionChange]
  )

  // Public navigation functions (stable references)
  const goToSection = useCallback(
    (index: number) => goToSectionInternal(index),
    [goToSectionInternal]
  )

  const nextSection = useCallback(() => {
    goToSectionInternal(currentSectionRef.current + 1)
  }, [goToSectionInternal])

  const prevSection = useCallback(() => {
    goToSectionInternal(currentSectionRef.current - 1)
  }, [goToSectionInternal])

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current !== null) {
        window.clearTimeout(cooldownRef.current)
        cooldownRef.current = null
      }
    }
  }, [])

  // GSAP Observer for wheel, touch, and pointer events
  useEffect(() => {
    if (typeof window === "undefined") return

    const target = containerRef?.current ?? document.documentElement

    // Navigation helpers (respect the in-flight animation guard and bounds)
    const goNext = () => {
      if (
        !isAnimatingRef.current &&
        currentSectionRef.current < totalSections - 1
      ) {
        goToSectionInternal(currentSectionRef.current + 1)
      }
    }
    const goPrev = () => {
      if (!isAnimatingRef.current && currentSectionRef.current > 0) {
        goToSectionInternal(currentSectionRef.current - 1)
      }
    }
    const isWheel = (self: { event?: Event | null }) =>
      (self?.event?.type || "").includes("wheel")

    // Create GSAP Observer for unified event handling.
    // GSAP's onDown/onUp report the RAW direction of the input:
    // - wheel: onDown = wheel scrolled down,        onUp = wheel scrolled up
    // - touch: onDown = finger dragged down,        onUp = finger dragged up
    //
    // For a mouse wheel, "scroll down" should advance to the NEXT section.
    // For touch, the natural gesture (and the previous Swiper behaviour) is the
    // opposite: dragging the finger UP advances to the NEXT section. So we keep
    // the wheel mapping but INVERT it for touch/pointer input.
    const observer = Observer.create({
      target,
      type: "wheel,touch,pointer",
      tolerance: touchThreshold,
      preventDefault: true,
      onDown: (self) => {
        // wheel down => next; touch drag down => previous
        if (isWheel(self)) goNext()
        else goPrev()
      },
      onUp: (self) => {
        // wheel up => previous; touch drag up => next
        if (isWheel(self)) goPrev()
        else goNext()
      },
      // Use onChange to check velocity/momentum for trackpads
      onChange: (self) => {
        // GSAP Observer provides velocity - we can use this for additional sensitivity control
        // The absolute deltaY tells us how strong the scroll/swipe was
        const absVelocity = Math.abs(self.velocityY)

        // For wheel events, check if the delta exceeds our threshold
        // This helps filter out minor trackpad momentum
        if (self.event?.type?.includes("wheel")) {
          const wheelEvent = self.event as WheelEvent
          if (Math.abs(wheelEvent.deltaY) < wheelThreshold) {
            return // Ignore small wheel movements
          }
        }
      },
    })

    return () => {
      observer.kill()
    }
  }, [
    containerRef,
    totalSections,
    goToSectionInternal,
    wheelThreshold,
    touchThreshold,
  ])

  // Keyboard event handler for arrow keys, Page Up/Down, Home, and End
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return
      }

      switch (e.key) {
        case "ArrowDown":
        case "PageDown":
          e.preventDefault()
          goToSectionInternal(currentSectionRef.current + 1)
          break
        case "ArrowUp":
        case "PageUp":
          e.preventDefault()
          goToSectionInternal(currentSectionRef.current - 1)
          break
        case "Home":
          e.preventDefault()
          goToSectionInternal(0)
          break
        case "End":
          e.preventDefault()
          goToSectionInternal(totalSections - 1)
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [goToSectionInternal, totalSections])

  // Update URL hash when section changes
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !sectionHashes ||
      isUpdatingFromHashRef.current
    ) {
      return
    }

    const hash = sectionHashes[currentSection]
    if (hash !== undefined) {
      const newUrl = `${window.location.pathname}${window.location.search}#${hash}`
      window.history.replaceState(null, "", newUrl)
    }
  }, [currentSection, sectionHashes])

  // Handle popstate (browser back/forward navigation)
  useEffect(() => {
    if (typeof window === "undefined" || !sectionHashes) {
      return
    }

    const handlePopState = () => {
      const hash = window.location.hash.slice(1)
      if (hash) {
        const index = sectionHashes.indexOf(hash)
        if (index !== -1 && index !== currentSectionRef.current) {
          isUpdatingFromHashRef.current = true
          goToSectionInternal(index)
          requestAnimationFrame(() => {
            isUpdatingFromHashRef.current = false
          })
        }
      }
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [sectionHashes, goToSectionInternal])

  return {
    currentSection,
    isAnimating,
    prefersReducedMotion,
    goToSection,
    nextSection,
    prevSection,
  }
}
